import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import type { OccupancyStatus, PortalStatus } from '@/lib/types'

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'gold'
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className = '',
  children,
  disabled,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md'; loading?: boolean }) {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-brand-500'
  const sizes = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm'
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700',
    secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    ghost: 'text-slate-600 hover:bg-slate-100',
    gold: 'bg-gradient-to-b from-amber-400 to-amber-600 text-black font-semibold shadow-sm hover:from-amber-300 hover:to-amber-500',
  }
  return (
    <button className={`${base} ${sizes} ${variants[variant]} ${className}`} disabled={disabled || loading} {...rest}>
      {loading && <Spinner className="h-3.5 w-3.5" />}
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------
export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin text-current ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}

export function PageSpinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-2 text-slate-400">
      <Spinner className="h-6 w-6" />
      <span className="text-sm">{label}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------
export function Badge({
  tone = 'slate',
  className = '',
  children,
}: {
  tone?: 'slate' | 'green' | 'amber' | 'red' | 'blue' | 'gold'
  className?: string
  children: React.ReactNode
}) {
  const tones: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-brand-100 text-brand-800',
    gold: 'bg-amber-950/50 text-amber-300 ring-1 ring-inset ring-amber-700/40',
  }
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]} ${className}`}>{children}</span>
}

export function PortalStatusBadge({ status }: { status: PortalStatus }) {
  const map: Record<PortalStatus, { tone: 'slate' | 'green' | 'amber' | 'red'; label: string }> = {
    draft: { tone: 'slate', label: 'Draft' },
    open: { tone: 'green', label: 'Open' },
    edit_only: { tone: 'amber', label: 'Edit Only' },
    closed: { tone: 'red', label: 'Closed' },
  }
  const m = map[status]
  return <Badge tone={m.tone}>{m.label}</Badge>
}

export function OccupancyBadge({ status }: { status: OccupancyStatus }) {
  const map: Record<OccupancyStatus, { tone: 'slate' | 'green' | 'amber'; label: string }> = {
    empty: { tone: 'slate', label: 'Empty' },
    partial: { tone: 'amber', label: 'Partial' },
    full: { tone: 'green', label: 'Full' },
  }
  const m = map[status]
  return <Badge tone={m.tone}>{m.label}</Badge>
}

// ---------------------------------------------------------------------------
// Card / StatTile
// ---------------------------------------------------------------------------
export function Card({ className = '', children, ...rest }: React.HTMLAttributes<HTMLDivElement> & { className?: string; children: React.ReactNode }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white ${className}`} {...rest}>
      {children}
    </div>
  )
}

export function StatTile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------
export function Field({
  label,
  hint,
  error,
  required,
  dark = false,
  children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  dark?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className={`mb-1 block text-sm font-medium ${dark ? 'text-amber-100/80' : 'text-slate-700'}`}>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && !error && <span className={`mt-1 block text-xs ${dark ? 'text-neutral-500' : 'text-slate-500'}`}>{hint}</span>}
      {error && <span className={`mt-1 block text-xs ${dark ? 'text-red-400' : 'text-red-600'}`}>{error}</span>}
    </label>
  )
}

const inputBase =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-400'

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input({ className = '', ...rest }, ref) {
  return <input ref={ref} className={`${inputBase} ${className}`} {...rest} />
})

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className = '', ...rest },
  ref
) {
  return <textarea ref={ref} className={`${inputBase} ${className}`} {...rest} />
})

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className = '', children, ...rest }, ref) {
  return (
    <select ref={ref} className={`${inputBase} bg-white ${className}`} {...rest}>
      {children}
    </select>
  )
})

export function Checkbox({ label, className = '', ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }) {
  return (
    <label className={`inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700 ${className}`}>
      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" {...rest} />
      {label}
    </label>
  )
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------
export function EmptyState({ title, description, action }: { title: string; description?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 px-6 py-14 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4 flex gap-2">{action}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'max-w-lg',
  dark = false,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
  width?: string
  dark?: boolean
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className={`absolute inset-0 ${dark ? 'bg-black/70' : 'bg-slate-900/40'}`} onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 w-full ${width} max-h-[85vh] overflow-y-auto rounded-lg shadow-xl ${dark ? 'border border-amber-900/30 bg-neutral-900' : 'bg-white'}`}
      >
        <div className={`flex items-center justify-between border-b px-5 py-3.5 ${dark ? 'border-amber-900/30' : 'border-slate-200'}`}>
          <h2 className={`text-sm font-semibold ${dark ? 'text-amber-50' : 'text-slate-900'}`}>{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className={dark ? 'rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300' : 'rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600'}
          >
            ✕
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className={`flex justify-end gap-2 border-t px-5 py-3 ${dark ? 'border-amber-900/30' : 'border-slate-200'}`}>{footer}</div>}
      </div>
    </div>
  )
}

export function useConfirm() {
  const [state, setState] = useState<{
    open: boolean
    title: string
    description?: string
    confirmLabel: string
    tone: 'danger' | 'primary'
    resolve?: (value: boolean) => void
  }>({ open: false, title: '', confirmLabel: 'Confirm', tone: 'primary' })

  const confirm = useCallback((opts: { title: string; description?: string; confirmLabel?: string; tone?: 'danger' | 'primary' }) => {
    return new Promise<boolean>((resolve) => {
      setState({ open: true, title: opts.title, description: opts.description, confirmLabel: opts.confirmLabel || 'Confirm', tone: opts.tone || 'primary', resolve })
    })
  }, [])

  const node = (
    <Modal
      open={state.open}
      onClose={() => {
        state.resolve?.(false)
        setState((s) => ({ ...s, open: false }))
      }}
      title={state.title}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              state.resolve?.(false)
              setState((s) => ({ ...s, open: false }))
            }}
          >
            Cancel
          </Button>
          <Button
            variant={state.tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => {
              state.resolve?.(true)
              setState((s) => ({ ...s, open: false }))
            }}
          >
            {state.confirmLabel}
          </Button>
        </>
      }
    >
      {state.description && <p className="text-sm text-slate-600">{state.description}</p>}
    </Modal>
  )

  return { confirm, confirmDialog: node }
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------
interface Toast {
  id: number
  message: string
  tone: 'success' | 'error' | 'info'
}
const ToastContext = createContext<{ push: (message: string, tone?: Toast['tone']) => void }>({ push: () => {} })

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const push = useCallback((message: string, tone: Toast['tone'] = 'success') => {
    const id = ++idRef.current
    setToasts((t) => [...t, { id, message, tone }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000)
  }, [])

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-md px-3.5 py-2.5 text-sm shadow-lg ${
              t.tone === 'success' ? 'bg-slate-900 text-white' : t.tone === 'error' ? 'bg-red-600 text-white' : 'bg-brand-600 text-white'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}

// ---------------------------------------------------------------------------
// Autosave status pill (spec section 20)
// ---------------------------------------------------------------------------
export type SaveState = 'idle' | 'saving' | 'saved' | 'error'
export function SaveStatus({ state, errorMessage }: { state: SaveState; errorMessage?: string }) {
  if (state === 'idle') return null
  if (state === 'saving') return <span className="text-xs text-slate-400">Saving…</span>
  if (state === 'saved') return <span className="text-xs text-emerald-600">Saved ✓</span>
  return <span className="text-xs text-red-600">{errorMessage || 'Save failed'}</span>
}
